import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SessionService } from '../../../../core/session/session.service';
import { LoginService } from '../../../login.service';
import { SettingsService } from '../../../../core/settings/settings.service';
import { Schema } from '../../../../core/_models/schema';
import { first, takeUntil } from 'rxjs/operators';
import { Angulartics2 } from 'angulartics2';
import { MemberPublicDataService } from '@app/core/recaptcha/member-public-data.service';
import { PASSWORD_INVALID_ERROR_KEY, PASSWORD_REQUIRED_ERROR_KEY } from '@app/+login/deprecated-hybrid/consts';
import { Subject } from 'rxjs';
import { AsyncPipe, NgClass } from '@angular/common';
import { IdentifierInputComponent } from '@app/+login/views/entry/login/identifier-input/identifier-input.component';
import { PasswordInputComponent } from '@app/+login/components/password-input/password-input.component';
import { TranslatePipe } from '@ngx-translate/core';

const MSISDN_IDENTIFIER = 'msisdn';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    NgClass,
    IdentifierInputComponent,
    PasswordInputComponent,
    TranslatePipe,
    FormsModule,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  public form: UntypedFormGroup;
  public showPassword = false;
  public isLoading = false;
  public canLogin: boolean;
  public properties: Schema;
  public settingsObservable = this.settingsService.getSettingsOnce();
  public sendOTP = false;
  public formErrors: { [key: string]: { error: string; [key: string]: unknown } } = {};
  public submitted = false;

  private unsubscribe$ = new Subject();

  constructor(formBuilder: UntypedFormBuilder,
              private route: ActivatedRoute,
              private sessionService: SessionService,
              private loginService: LoginService,
              private settingsService: SettingsService,
              private angulartics: Angulartics2,
              private memberPublicDataService: MemberPublicDataService) {
    this.form = formBuilder.group({ identifier: '', password: '' });

    this.settingsObservable.pipe(
      first(),
    ).subscribe((settings) => {
      this.properties = settings.schemaObject;
      const identifiers = settings.schema.identifiers;
      this.sendOTP = identifiers?.[0] === MSISDN_IDENTIFIER;
      this.route.queryParamMap.subscribe(
        (params) => {
          this.loginService.parseQueryParams(params, this.getIdentifierField(), identifiers);
        },
      );
    });
  }

  public ngOnInit(): void {
    this.loginService.checkIfMemberBasicallyAuthenticated(this.route);
    this.angulartics.eventTrack.next({ action: 'member_main_page' });
    this.clearFormErrorsOnValueChange();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  public onSubmit(): void {
    this.submitted = true;

    if (!this.getIdentifierField()?.valid) {
      const errors = Object.entries(this.getIdentifierField()?.errors || {});
      if (errors.length > 0) {
        const [errorName, property] = errors[0];
        this.formErrors.identifier = {
          error: errorName === 'error' ? 'invalid_msisdn' : errorName,
          property,
        };
      }
      return;
    }

    this.isLoading = true;
    this.showPassword ? this.signIn() : this.isMember();
  }

  public onBackClicked(): void {
    this.showPassword = false;
    this.formErrors = {};
    this.getPasswordField()?.patchValue('');
  }

  private isMember(): void {
    this.angulartics.eventTrack.next({ action: 'next_page_main' });

    this.memberPublicDataService.getMemberPublicInfo(this.getIdentifierField()?.value).subscribe((info) => {
      this.canLogin = info.can_login == null ? true : info.can_login;
      const isMember = info.exists;

      if (isMember) {
        this.showPassword = true;
      } else {
        this.loginService.navigateToNewProfile(this.getIdentifierField()?.value, this.route);
      }
      this.isLoading = false;
    }, (response) => {
      this.isLoading = false;
      this.submitted = true;
      if (response.status === 500) {
        this.formErrors.identifier = { 'error': 'service_unavailable', 'property': 'identifier' };
      } else {
        this.formErrors.identifier = { 'error': 'invalid_msisdn', 'property': 'identifier' };
      }
    });
  }

  private signIn(): void {
    if (!this.getPasswordField()?.value || this.getPasswordField()?.value === '') {
      this.formErrors.password = { error: PASSWORD_REQUIRED_ERROR_KEY };
      this.isLoading = false;
      return;
    }

    this.sessionService.deprecatedSignIn(
      this.getIdentifierField()?.value,
      this.getPasswordField()?.value,
      this.sessionService.getOtpSecret(),
    ).subscribe(
      () => this.loginService.afterSignedIn(this.route),
      (response) => {
        this.isLoading = false;
        if (response.status === 401) {
          this.formErrors.password = { error: PASSWORD_INVALID_ERROR_KEY };
        }
      },
    );
  }

  protected getIdentifierField(): AbstractControl | null {
    return this.form.get('identifier');
  }

  private getPasswordField(): AbstractControl | null {
    return this.form.get('password');
  }

  private clearFormErrorsOnValueChange(): void {
    this.form.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(() => this.formErrors = {});
  }
}
